import React from "react";
import {
  Dialog,
  DialogContent,
  Box,
  Typography,
  Tabs,
  Tab,
  Divider,
  Button,
  Avatar,
  IconButton,
  Switch
} from "@mui/material";
import CloseIcon from "@mui/icons-material/Close";

const SETTINGS_TABS = [
  { label: "General" },
  { label: "Profile" },
  { label: "About" }
];

interface SettingsModalProps {
  open: boolean;
  onClose: () => void;
  fullName?: string;
  username?: string;
  email?: string;
  onLogout: () => void;
  onOpenChangePw: () => void;
}

export default function SettingsModal({
  open,
  onClose,
  fullName,
  username,
  email,
  onLogout,
  onOpenChangePw
}: SettingsModalProps) {
  const [tab, setTab] = React.useState(1);

  const initials = (fullName || username || "U").charAt(0).toUpperCase();

  return (
    <Dialog
      open={open}
      onClose={onClose}
      maxWidth="xs"
      fullWidth
      PaperProps={{
        sx: {
          borderRadius: 4,
          zIndex: 1300, 
          position: "relative",
          p: 0,
          boxShadow: "0px 6px 32px rgba(25, 118, 210, 0.13)",
          minWidth: 370,
          maxWidth: 420,
        }
      }}
    >
      <Box sx={{ position: "relative", pt: 3, pb: 2, px: 0, bgcolor: "#fff" }}>
        <IconButton
          onClick={onClose}
          sx={{
            position: "absolute",
            right: 16,
            top: 12,
            color: "#9099ab",
            "&:hover": {
              backgroundColor: "#f0f0f0",
              color: "#000",
            },
            cursor: "pointer", 
            zIndex: 2000, 
          }}
        >
          <CloseIcon />
        </IconButton>

        <Tabs
          value={tab}
          onChange={(_, v) => setTab(v)}
          centered
          sx={{
            mb: 2,
            ".MuiTabs-indicator": {
              background: "#1976d2",
              height: 3,
              borderRadius: 2,
            },
            ".MuiTab-root": {
              fontWeight: 500,
              color: "#69778b",
              fontSize: 15,
              minHeight: 36,
              textTransform: "none",
              "&.Mui-selected": {
                color: "#1976d2",
              },
            },
          }}
        >
          {SETTINGS_TABS.map((t) => (
            <Tab label={t.label} key={t.label} />
          ))}
        </Tabs>

        <DialogContent
          sx={{
            pt: 1, pb: 2.5, px: 3,
            minHeight: 270,
            overflowY: "visible",
            bgcolor: "#fff",
          }}
        >
          {/* Profile tab */}
          {tab === 1 && (
            <Box>
              <Box sx={{
                display: 'flex',
                alignItems: 'center',
                gap: 2,
                mb: 2
              }}>
                <Avatar
                  sx={{
                    bgcolor: "#1976d2",
                    width: 52,
                    height: 52,
                    fontSize: 23,
                    fontWeight: 700
                  }}
                >
                  {initials}
                </Avatar>
                <Box>
                  <Typography sx={{ fontWeight: 700, fontSize: 20, mb: 0.1 }}>
                    {fullName || "User"}
                  </Typography>
                  <Typography sx={{ fontSize: 14, color: "#8a95ad" }}>
                    @{username}
                  </Typography>
                </Box>
              </Box>
              <Divider sx={{ mb: 2 }} />
              <Box sx={{ mb: 1.5 }}>
                <Typography sx={{ fontSize: 15, color: "#7b8794", mb: 0.5 }}>Email</Typography>
                <Typography sx={{ fontSize: 16 }}>{email}</Typography>
              </Box>
              <Divider sx={{ my: 2 }} />
              <Button
                variant="outlined"
                fullWidth
                onClick={onOpenChangePw}
                sx={{
                  mb: 1.5,
                  textTransform: "none",
                  borderRadius: 2,
                  fontSize: 15,
                  color: "#1c3477",
                  borderColor: "#1c3477"
                }}
              >
                Change password
              </Button>
              <Button
                variant="outlined"
                color="error"
                fullWidth
                onClick={onLogout}
                sx={{
                  mb: 1,
                  textTransform: "none",
                  borderRadius: 2,
                  fontSize: 15,
                  borderColor: "#f8d7da"
                }}
              >
                Log out
              </Button>
              <Button
                variant="text"
                color="error"
                fullWidth
                sx={{
                  textTransform: "none",
                  borderRadius: 2,
                  fontSize: 14,
                  fontWeight: 400
                }}
              >
                Delete account
              </Button>
            </Box>
          )}

          {/* General tab */}
          {tab === 0 && (
            <Box>
              <Typography sx={{ fontSize: 16, fontWeight: 600, mb: 2, color: "#222f3e" }}>General Settings</Typography>
              <Box sx={{
                display: "flex", alignItems: "center", justifyContent: "space-between",
                py: 1.5, borderBottom: "1px solid #f2f3f5"
              }}>
                <Box>
                  <Typography sx={{ fontSize: 15, fontWeight: 500 }}>Enable notifications</Typography>
                  <Typography sx={{ fontSize: 13.5, color: "#8a95ad" }}>
                    Allow email or push notifications
                  </Typography>
                </Box>
                <Switch />
              </Box>
            </Box>
          )}

          {/* About tab */}
          {tab === 2 && (
            <Box>
              <Typography sx={{ fontWeight: 700, fontSize: 18, mb: 1.5, color: "#1c3477" }}>About</Typography>
              <Typography sx={{ color: "#7b8794", fontSize: 14, mb: 2 }}>
                Version: 0.0.1<br />
                <br />
                For support, contact: <b>info@tecnovation.com</b>
              </Typography>
              <Divider sx={{ my: 2 }} />
              <Typography sx={{ fontSize: 13, color: "#b0b8c1" }}>
                Copyright &copy; 2025 Tecnovation.
              </Typography>
            </Box>
          )}
        </DialogContent>
      </Box>
    </Dialog>
  );
}
